import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import toastHandler from 'Utils/toastHandler';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import { ObrasApiV2 } from 'Data/Obras.api.v2';
import { ISettingsStageRequest } from 'Data/interfaces/Obra/IObraStages';
import { IObraData } from 'Data/interfaces/Obra/IObraData';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';

export const useQuerySettingsStages = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: ISettingsStageRequest) => {
      return ObrasApiV2.settingsStages(variables);
    },
    onMutate: async (variables) => {
      const previousObras = queryClient.getQueryData<IObraData>([OBRA_QUERY_KEY]);
      const previousUniqueObra = queryClient.getQueryData<IObraData>(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
      );

      const configUpdate = {
        ValidateStagesInNomenclature: variables.config.validateStagesInNomenclature,
        AllowOnlyStagesLinkedToDisciplinesInUpload: variables.config.allowOnlyStagesLinkedToDisciplinesInUpload,
        AllowOnlyStagesLinkedToDisciplinesInTopics: variables.config.allowOnlyStagesLinkedToDisciplinesInTopics,
      };

      queryClient.setQueryData<IObraData[]>([OBRA_QUERY_KEY], (oldData) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === variables.constructionStieId) {
            return {
              ...obra,
              ...configUpdate,
            };
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
        (oldData) => {
          if (!oldData) { return undefined; }
          return {
            ...oldData,
            ...configUpdate,
          };
        },
      );

      return { previousObras, previousUniqueObra };
    },
    onSuccess: async () => {
      toastHandler.showSuccess('Configuração alterada com sucesso!');
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({
          queryKey: [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
        }),
      ]);

      queryClient.setQueryData([OBRA_QUERY_KEY], context?.previousObras);
      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
        context?.previousUniqueObra,
      );

      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao atualizar configuração da Etapa!';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    settingsStages: mutate,
    isLoading: isPending,
  };
};
